import { Dispatch, useEffect, useState } from "react";

type Options = {
  mode?: "session" | "local";
};

export function usePersistentState<T>(
  key: string,
  options?: Options,
): [T | undefined, Dispatch<T>];

export function usePersistentState<T>(
  key: string,
  initialValue: T,
  options?: Options,
): [T, Dispatch<T>];

export function usePersistentState<T>(
  key: string,
  initialValue?: T,
  options?: Options,
): [T | undefined, Dispatch<T>] {
  const { mode = "local" } = options || {};
  const storage =
    typeof window !== "undefined" &&
    (mode === "session" ? window.sessionStorage : window.localStorage);

  const [value, setValue] = useState<T | undefined>(() => {
    if (typeof window === "undefined" || !storage) {
      return initialValue;
    }

    const item = storage.getItem(key);

    return item ? (JSON.parse(item) as T) : initialValue;
  });

  useEffect(() => {
    if (value === undefined || typeof window === "undefined" || !storage) {
      return;
    }

    storage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  const handleSetValue: Dispatch<T> = (newValue) => {
    if (storage) {
      storage.setItem(key, JSON.stringify(newValue));
    }

    setValue(newValue);
  };

  return [value, handleSetValue];
}
